import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "hooks/useApi";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useNotification } from "./useNotification";
import { useRosterAndStaffQueryDataUpdater } from "./useRosterAndStaffQueryDataUpdater";

type Props = {
  entityId: string;
  seasonId: string;
};

export const useFetchSeasonStaff = ({ entityId, seasonId }: Props) => {
  const callApi = useApi();
  const { sport, organizationId } = useParams();

  return useQuery({
    queryKey: ["seasonStaff", sport, organizationId, entityId, seasonId],
    queryFn: () =>
      callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/staff`, {
        defaultErrorMessage: "Can't fetch season staff",
      }),
  });
};

export const useFetchPublishedSeasonStaff = ({ entityId, seasonId, enabled }: Props & { enabled: boolean }) => {
  const callApi = useApi();
  const { sport, organizationId } = useParams();

  return useQuery({
    queryKey: ["seasonStaff", "published", sport, organizationId, entityId, seasonId],
    queryFn: () =>
      callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/staff/published`, {
        defaultErrorMessage: "Can't fetch published season staff",
      }),
    enabled,
  });
};

export const useDeleteSeasonStaffPersons = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();
  const { deleteRecords } = useRosterAndStaffQueryDataUpdater();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entityId, seasonId, data }: Props & { data: any }) => {
      return callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/staff`, {
        method: "DELETE",
        body: data,
      });
    },
    onSuccess: (response: any, { entityId, seasonId, data }: Props & { data: any }) => {
      const result = deleteRecords(
        ["seasonStaff", sport, organizationId, entityId, seasonId],
        response,
        data?.personIds,
        data?.dcPersonIds
      );
      if (result) {
        queryClient.invalidateQueries({
          queryKey: ["season", sport, organizationId, entityId, seasonId],
        });
        showSuccessNotification(formatMessage({ id: "notification.staffPerson.deleted" }));
      }
    },
  });
};

export const useAddSeasonStaffPersons = () => {
  const { sport, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();
  const { addRecords } = useRosterAndStaffQueryDataUpdater();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entityId, seasonId, data }: Props & { data: any }) => {
      return callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/staff`, {
        method: "POST",
        body: data,
      });
    },
    onSuccess: (response: any, { entityId, seasonId }: Props & { data: any }) => {
      const result = addRecords(["seasonStaff", sport, organizationId, entityId, seasonId], response);
      if (result) {
        queryClient.invalidateQueries({
          queryKey: ["season", sport, organizationId, entityId, seasonId],
        });
        showSuccessNotification(formatMessage({ id: "notification.staffPerson.added" }));
      }
    },
  });
};

export const useUpdateSeasonStaffPersonInternal = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();
  const { replaceRecords } = useRosterAndStaffQueryDataUpdater();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entityId, seasonId, personId, data }: Props & { personId: string; data: any }) =>
      callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/staff/${personId}`, {
        method: "PUT",
        body: data,
      }),
    onSuccess: (response: any, { entityId, seasonId }: Props & { personId: string; data: any }) => {
      replaceRecords(["seasonStaff", sport, organizationId, entityId, seasonId], response);
      queryClient.invalidateQueries({
        queryKey: ["season", sport, organizationId, entityId, seasonId],
      });
      showSuccessNotification(formatMessage({ id: "notification.staffPerson.updated" }));
    },
  });
};

export const useUpdateSeasonStaffPersonDc = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();
  const { replaceRecords } = useRosterAndStaffQueryDataUpdater();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entityId, seasonId, dcPersonId, data }: Props & { dcPersonId: string; data: any }) =>
      callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/staff/dc/${dcPersonId}`, {
        method: "PUT",
        body: data,
      }),
    onSuccess: (response: any, { entityId, seasonId }: Props & { dcPersonId: string; data: any }) => {
      replaceRecords(["seasonStaff", sport, organizationId, entityId, seasonId], response);
      queryClient.invalidateQueries({
        queryKey: ["season", sport, organizationId, entityId, seasonId],
      });
      showSuccessNotification(formatMessage({ id: "notification.staffPerson.updated" }));
    },
  });
};
